import React, { useState, useEffect } from 'react';
import { Service, ServiceRequest } from '../lib/tenant-api';
import Modal from './Modal';
import clsx from 'clsx';

interface EditServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ServiceRequest) => Promise<void>;
  service: Service;
}

function getServiceSourceLabel(service: Service): string {
  const haystack = `${service.source_file_name || ''} ${service.category || ''}`.toLowerCase();

  if (haystack.includes('hubspot')) return 'Hubspot';
  if (haystack.includes('google')) return 'Google drive';
  if (haystack.includes('evolved') || haystack.includes('wordpress') || haystack.includes(' wp')) {
    return 'Evolved WP URL';
  }
  if (haystack.includes('webflow')) return 'Webflow lawma integration';
  if (haystack.includes('shopify')) return 'Shopify webstore';

  return 'Upload';
}

const EditServiceModal: React.FC<EditServiceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  service,
}) => {
  const [formData, setFormData] = useState<ServiceRequest>({
    name: '',
    description: '',
    category: '',
    pricing_model: '',
    status: 'active',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        description: service.description || '',
        category: service.category || '',
        pricing_model: service.pricing_model || '',
        features: service.features || [],
        status: service.status,
      });
    }
  }, [service]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to update service';
      setError(message || 'Failed to update service');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ServiceRequest, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="edit-service-form"
        disabled={isLoading || !formData.name.trim()}
        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {isLoading ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit service"
      footer={footer}
      size="xl"
      contentClassName="flex-1 overflow-y-auto px-4 sm:px-6"
    >
      <form id="edit-service-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-2 lg:divide-x lg:divide-gray-200">
          <div className="space-y-5 py-4 lg:pr-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Service name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                Source
              </label>
              <input
                type="text"
                id="source"
                value={getServiceSourceLabel(service)}
                readOnly
                className="mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                disabled={isLoading}
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label htmlFor="pricing_model" className="block text-sm font-medium text-gray-700">
                Pricing model
              </label>
              <select
                id="pricing_model"
                value={formData.pricing_model}
                onChange={(e) => handleInputChange('pricing_model', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                disabled={isLoading}
              >
                <option value="">Select pricing model</option>
                <option value="fixed">Fixed price</option>
                <option value="hourly">Hourly rate</option>
                <option value="monthly">Monthly subscription</option>
                <option value="yearly">Yearly subscription</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div className="mt-6 bg-gray-50 lg:mt-0">
            <div className="px-4 py-4 lg:pl-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <textarea
                id="description"
                rows={16}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={clsx(
                  'mt-1 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm',
                  'focus:border-gray-200 focus:outline-none focus:ring-0',
                  'min-h-[320px] lg:min-h-[420px]'
                )}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="pb-4">
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditServiceModal;
